import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Users, Lock, TrendingUp, MessageSquare, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, GainBadge, EmptyState } from "../components/ui";
import Disclaimer from "../components/Disclaimer";
import CommentThread from "../components/CommentThread";
import RichText, { toPlainText } from "../components/RichText";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { money, shortDate, num } from "../lib/format";
import VerifiedBadge from "../components/VerifiedBadge";
import { useTranslation } from "react-i18next";
import useSeo from "../lib/useSeo";
import TierBadge from "../components/TierBadge";
import InvestorBenchmark from "../components/InvestorBenchmark";
import Reactions from "../components/Reactions";
import MessageRequestDialog from "../components/MessageRequestDialog";

// Coarse performance buckets are all a free viewer gets — deliberately not a number.
const BAND_STYLE = {
  strong_gain: "bg-emerald-100 text-emerald-700",
  gain: "bg-emerald-50 text-emerald-600",
  flat: "bg-slate-100 text-slate-500",
  loss: "bg-rose-50 text-rose-600",
  strong_loss: "bg-rose-100 text-rose-700",
};

function LockedRow({ entry, t }) {
  return (
    <div className="card p-4 flex items-center gap-3" data-testid={`locked-${entry.id}`}>
      <div className="w-9 h-9 rounded-xl bg-slate-100 grid place-items-center shrink-0">
        <Lock size={15} className="text-slate-400" />
      </div>
      <div className="min-w-0 flex-1">
        {/* Intentionally blurred bars: there is no hidden text here to reveal — the
            server never sent the ticker, price or size. */}
        <div className="flex items-center gap-2">
          <span className={`pill ${entry.type === "buy" ? "bg-brand-50 text-brand-600" : "bg-amber-50 text-amber-700"}`}>
            {entry.type.toUpperCase()}
          </span>
          <div className="h-3 w-24 rounded bg-slate-200/80" />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="h-2.5 w-16 rounded bg-slate-100" />
          <div className="h-2.5 w-12 rounded bg-slate-100" />
        </div>
      </div>
      <div className="text-right shrink-0">
        <span className={`pill ${BAND_STYLE[entry.performance_band] || BAND_STYLE.flat}`}>
          {t(`investors.band.${entry.performance_band}`)}
        </span>
        <div className="text-[11px] text-slate-400 mt-1">{entry.age}</div>
      </div>
    </div>
  );
}

/**
 * Comments on a shared position. The rendering, threading and reply handling all live
 * in `CommentThread`, which posts and positions share — this is just the loading and
 * the endpoint it talks to.
 */
function Comments({ txId, canComment, t }) {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.positionComments(txId).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [txId]);
  useEffect(() => { load(); }, [load]);

  const add = async (body, parentId) => {
    try {
      const r = await api.addComment(txId, { body, parent_id: parentId || null });
      toast.success(r.data?.status === "approved" ? t("comments.posted") : t("investors.commentPending"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
    }
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <CommentThread comments={items} canComment={canComment} onAdd={add}
                     idPrefix={`comment`} emptyText={t("posts.noComments")} />
    </div>
  );
}

export default function InvestorDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { isPaid, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openComments, setOpenComments] = useState(null);
  const [dmOpen, setDmOpen] = useState(false);

  // The bio is HTML, and a meta description cannot render it — slicing the raw string
  // put `<p><strong>` into the Google result and every shared link preview. Same leak
  // as the page had, somewhere nobody would ever look.
  useSeo({
    title: data?.display_name,
    description: data?.headline || toPlainText(data?.bio, 160),
    jsonLd: data ? {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      mainEntity: {
        "@type": "Person",
        name: data.display_name,
        description: data.headline || toPlainText(data.bio, 300),
      },
    } : null,
  });

  const load = useCallback(() => {
    api.investor(id).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const startTrial = async () => {
    try {
      await api.startTrial(id);
      toast.success(t("investors.trialStarted", { days: 7 }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const toggleFollow = async () => {
    try {
      if (data.is_following) { await api.unfollowInvestor(id); }
      else { await api.followInvestor(id); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-24" /></div>;
  if (!data) return <EmptyState testid="investor-404" title={t("investors.notFoundTitle")} description={t("investors.notFoundDesc")} />;

  const full = data.visibility === "full";

  return (
    <div className="space-y-6">
      <Link to="/investors" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-600">
        <ArrowLeft size={16} /> {t("investors.backToList")}
      </Link>

      <Panel className="card p-5 sm:p-6" testid="investor-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-heading font-semibold tracking-tight">{data.display_name}</h1>
              {data.verified && <VerifiedBadge size={18} />}
              <TierBadge tier={data.tier} />
            </div>
            {data.headline && <p className="text-slate-500 mt-1">{data.headline}</p>}
            <div className="flex items-center gap-4 mt-3 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><Users size={14} />{data.follower_count} {t("investors.followers")}</span>
              <span className="flex items-center gap-1.5"><TrendingUp size={14} />{data.position_count} {t("investors.positions")}</span>
            </div>
          </div>
          {/* Bounded, or the column stretches to whatever the longest button label
              happens to be — "Message this investor" was pulling the whole header
              layout across and leaving Follow the width of a banner. Full width on a
              phone, a fixed action column from sm: up. */}
          <div className="w-full sm:w-auto sm:min-w-[180px] sm:max-w-[220px] sm:text-right">
            {data.return_pct !== null && data.return_pct !== undefined && (
              <div className="sm:text-right">
                <GainBadge value={data.return_pct} />
                <div className="text-[11px] text-slate-400 mt-1">{t("investors.perYear")}</div>
              </div>
            )}
            <button
              data-testid="follow-btn"
              onClick={toggleFollow}
              className={`mt-3 flex w-full ${data.is_following ? "btn-outline" : "btn-primary"} justify-center`}
            >
              {data.is_following ? t("investors.following") : t("investors.follow")}
            </button>
            {/* Shown to everyone, including free members: the dialog explains what
                Full Access buys far better than a hidden button does. What it never
                does is spend the slot — that takes the confirmation inside.
                Quieter than Follow on purpose: following is the action this page is
                for, messaging is the one you take after deciding. */}
            {user?.id !== id && (
              <button
                data-testid="dm-open-btn"
                onClick={() => setDmOpen(true)}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2
                           text-sm font-medium text-slate-500 hover:text-brand-600 hover:bg-slate-50
                           transition-colors"
              >
                <MessagesSquare size={15} className="shrink-0" />
                <span className="truncate">{t("dm.messageInvestor")}</span>
              </button>
            )}
          </div>
        </div>
        {/* Bio and strategy are written in the studio's rich-text editor, so they are
            HTML. They were printed as text — a reader saw a literal `<p><strong>` on
            the page that is meant to introduce the investor. RichText is the only
            component that renders authored markup; see components/RichText.jsx. */}
        {data.bio && <RichText html={data.bio} className="text-sm text-slate-600 mt-4" testid="investor-bio" />}
        {data.strategy && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t("investors.strategy")}
            </div>
            <RichText html={data.strategy} className="text-sm text-slate-600 mt-1.5"
                      testid="investor-strategy" />
          </div>
        )}
      </Panel>

      {data.trial_active && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3.5 text-sm text-emerald-800"
             data-testid="trial-active-banner">
          {t("investors.trialActive", { date: shortDate(data.trial_expires_at) })}
        </div>
      )}

      {/* Above the paywall on purpose: the comparison is the reason to subscribe, and
          two percentage curves disclose nothing about the positions themselves. */}
      <InvestorBenchmark investorId={data.investor_id} />

      {!full && data.locked_count > 0 && (
        <Panel className="card p-5 bg-brand-50/50 border-brand-100" testid="detail-paywall">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex gap-3">
              <Lock size={20} className="text-brand-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-slate-900">
                  {t("investors.lockedTitle", { count: data.locked_count })}
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {isPaid ? t("investors.followToUnlock") : t("investors.subscribeToUnlock")}
                </p>
              </div>
            </div>
            {isPaid ? (
              <button className="btn-primary shrink-0 justify-center" onClick={toggleFollow}>
                {t("investors.follow")}
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                {/* The trial is the primary action for a free user — seeing one
                    investor's real reasoning converts far better than a price page. */}
                {data.trial_available && (
                  <button className="btn-primary justify-center" onClick={startTrial} data-testid="start-trial-btn">
                    {t("investors.tryFree", { days: 7 })}
                  </button>
                )}
                <Link to="/billing"
                      className={`${data.trial_available ? "btn-outline" : "btn-primary"} justify-center`}
                      data-testid="detail-upgrade-btn">
                  {t("billing.upgrade")}
                </Link>
              </div>
            )}
          </div>
        </Panel>
      )}

      <div className="space-y-3" data-testid="investor-activity">
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("investors.activity")}</h2>
        {data.activity.length === 0 && (
          <div className="text-sm text-slate-400 py-6 text-center">{t("investors.noActivity")}</div>
        )}
        {data.activity.map((entry) =>
          entry.locked ? (
            <LockedRow key={entry.id} entry={entry} t={t} />
          ) : (
            <div key={entry.id} className="card p-4" data-testid={`activity-${entry.id}`}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`pill ${entry.type === "buy" ? "bg-brand-50 text-brand-600" : "bg-amber-50 text-amber-700"}`}>
                      {entry.type.toUpperCase()}
                    </span>
                    <Link to={`/instrument/${entry.ticker}`} className="font-medium text-slate-900 hover:text-brand-600">
                      {entry.ticker}
                    </Link>
                    <span className="text-xs text-slate-400 truncate">{entry.name}</span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1.5 tabular">
                    {num(entry.quantity, 4)} @ {money(entry.price, entry.currency)} · {shortDate(entry.date)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {entry.gain_pct !== null && entry.gain_pct !== undefined && <GainBadge value={entry.gain_pct} />}
                </div>
              </div>

              {entry.commentary && (entry.commentary.title || entry.commentary.body) && (
                <div className="mt-3 rounded-xl bg-slate-50 p-3.5">
                  {entry.commentary.title && (
                    <div className="font-medium text-slate-800 text-sm">{entry.commentary.title}</div>
                  )}
                  {entry.commentary.body && (
                    // Also authored HTML (sanitised by `upsert_commentary`), and it
                    // was leaking tags in exactly the same way as the bio.
                    <RichText html={entry.commentary.body} className="text-sm text-slate-600 mt-1"
                              testid={`commentary-${entry.id}`} />
                  )}
                  {entry.commentary.sentiment && (
                    <span className="pill bg-white text-slate-500 mt-2 inline-block">
                      {t(`investors.sentiment.${entry.commentary.sentiment}`)}
                    </span>
                  )}
                </div>
              )}

              <Reactions targetType="position" targetId={entry.id} className="mt-3" />

              <button
                onClick={() => setOpenComments(openComments === entry.id ? null : entry.id)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:text-brand-700"
                data-testid={`toggle-comments-${entry.id}`}
              >
                <MessageSquare size={13} /> {t("investors.comments")}
              </button>
              {openComments === entry.id && <Comments txId={entry.id} canComment={isPaid} t={t} />}
            </div>
          )
        )}
      </div>

      {/* Their writing, on their page. Same serializer as the posts list, so a locked
          post shows its excerpt and the paywall exactly as it does elsewhere. */}
      {data.posts?.length > 0 && (
        <Panel className="card p-5 sm:p-6" testid="investor-posts">
          <h2 className="font-heading font-semibold tracking-tight text-lg mb-1">
            {t("investors.postsTitle")}
          </h2>
          <p className="text-sm text-slate-500 mb-3">{t("investors.postsBody")}</p>
          <div className="divide-y divide-slate-50">
            {data.posts.map((p) => (
              <Link key={p.slug} to={`/posts/${p.slug}`} data-testid={`investor-post-${p.slug}`}
                    className="block py-3 group">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 group-hover:text-brand-600">{p.title}</div>
                    <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{p.excerpt}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                      {p.published_at && <span>{shortDate(p.published_at)}</span>}
                      {p.chart_count > 0 && <span>· {t("postCharts.lockedCount", { count: p.chart_count })}</span>}
                      {p.comment_count > 0 && <span>· {p.comment_count}</span>}
                    </div>
                  </div>
                  {p.locked && <Lock size={14} className="text-slate-300 shrink-0 mt-1" />}
                </div>
              </Link>
            ))}
          </div>
        </Panel>
      )}

      <Disclaimer />

      <MessageRequestDialog
        open={dmOpen}
        onClose={() => setDmOpen(false)}
        investorId={id}
        investorName={data.display_name}
      />
    </div>
  );
}
